import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../dataManagement/uiDb/UiDb";
import ProductDetailHelper from "../../../../../services/helpers/ProductDetailHelper";
import { filterSpecAttributes, extractRentalPricing } from "../utils/SpecsFilter";
import genericUtility from "../../../../../services/utility/GenericUtility";

/** Accepts the flag under either name the backend may use; missing = true
 *  (TEMP default until the product response carries it). */
function resolveSlotCalendarEnabled(product: unknown): boolean {
  const rec = (product ?? {}) as Record<string, unknown>;
  const value = rec.slotCalendarEnabled ?? rec.enableSlotCalendar;
  return typeof value === "boolean" ? value : true;
}

export interface UseProductInfoResult {
  title: string;
  price: number;
  isNegotiable: boolean;
  listingType: string;
  description: string;
  age: string;
  category: string;
  condition: string;
  postedDate: string;
  location: string;
  lat: number | undefined;
  lng: number | undefined;
  sellerName: string;
  sellerId: string;
  sellerVerified: boolean;
  likesCount: number;
  isTrustedSeller: boolean;
  isCurrentUserSeller: boolean;
  /** Listing has been marked SOLD. Anyone can still view the product, but it
   *  must not take new inquiries (no offer/contact CTA, no new questions). */
  isSold: boolean;
  queries: ReturnType<typeof ProductDetailHelper.retrieveQueries>;
  specAttributes: ReturnType<typeof filterSpecAttributes>;
  rentalPricing: ReturnType<typeof extractRentalPricing>;
  details: Record<string, any> | undefined;
  productId: string;
  displayPrice: boolean;
  contactMode: 'AUTO' | 'MANUAL';
  phone: string;
  businessId: string | undefined;
  businessName: string | undefined;
  businessLogoUrl: string | undefined;
  /** The listing's business has the availability calendar turned on
   *  (business config `enableSlotCalendar`, resolved into the product
   *  response). TEMP: defaults to true until the backend sends it. */
  slotCalendarEnabled: boolean;
}

export function useProductInfo(): UseProductInfoResult {
  const activeProductId = useSelector(
    (state: RootState) => state.productDetails.activeProductId
  );

  const product = useSelector((state: RootState) =>
    activeProductId ? state.productDetails.data[activeProductId] : undefined
  );

  return useMemo(() => {
    if (!product) {
      return {
        title: "", price: 0, isNegotiable: false, listingType: "",
        description: "", age: "", category: "", condition: "",
        postedDate: "", location: "", lat: undefined, lng: undefined,
        sellerName: "", sellerId: "", sellerVerified: false,
        likesCount: 0, isTrustedSeller: false, isCurrentUserSeller: false,
        isSold: false,
        queries: [], specAttributes: [], rentalPricing: [], details: undefined,
        productId: activeProductId ?? "",
        displayPrice: true,
        contactMode: "AUTO" as const,
        phone: "",
        businessId: undefined,
        businessName: undefined,
        businessLogoUrl: undefined,
        slotCalendarEnabled: false,
      };
    }

    const info = ProductDetailHelper.retrieveProductDetailsInfo(product);
    const { likesCount, isTrustedSeller } = ProductDetailHelper.retrieveLikesAndTrust(product);
    const queries = ProductDetailHelper.retrieveQueries(product);
    const isCurrentUserSeller = ProductDetailHelper.isCurrentUserSeller(product);
    const details = (product as any).details as Record<string, any> | undefined;

    const price = typeof info.price === "number"
      ? info.price
      : (info.price as any)?.askPrice ?? 0;

    const isNegotiable = typeof info.price === "object" && !!(info.price as any)?.negotiable;

    return {
      title: info.name ?? product.title ?? "",
      price,
      isNegotiable,
      listingType: product.listingType ?? "",
      description: info.description ?? product.description ?? "",
      age: String(info.age ?? product.age ?? ""),
      category: info.category ?? product.category ?? "",
      condition: info.condition ?? product.condition ?? "",
      postedDate: info.postedDate ?? (product as any).createdTime ?? "",
      location: info.location ?? product.location ?? "",
      lat: (product as any).latitude,
      lng: (product as any).longitude,
      sellerName: info.sellerName ?? product.sellerName ?? "",
      sellerId: info.sellerId ?? product.sellerId ?? "",
      sellerVerified: info.sellerVerified ?? product.sellerVerified ?? false,
      likesCount,
      isTrustedSeller,
      isCurrentUserSeller,
      isSold: (product as any).listingStatus === "SOLD",
      queries,
      specAttributes: filterSpecAttributes(details),
      rentalPricing: extractRentalPricing(details),
      details,
      productId: product.productId,
      displayPrice: product.displayPrice ?? true,
      contactMode: (product.contactMode ?? "AUTO") as "AUTO" | "MANUAL",
      phone: product.phone ?? "",
      businessId: (product as any).businessId as string | undefined,
      businessName: (product as any).businessName as string | undefined,
      businessLogoUrl: (product as any).businessLogoUrl as string | undefined,
      slotCalendarEnabled: resolveSlotCalendarEnabled(product),
    };
  }, [product, activeProductId]);
}
